import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import throttle from 'lodash/throttle';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Section } from 'src/app/components/Elements/Section';
import Spinner from 'src/app/components/Elements/Spinner';
import { SignUpForm } from 'src/app/components/Pages/PromotionalLandingPage/SignUpForm/container';
import { smoothFocus } from 'src/app/utils/smoothFocus';
import { theme } from 'src/styles/theme';

import {
  ccaPharmacyContent,
  defaultPharmacyContent,
  ELEMENT_IDS,
  employerPharmacyContent,
  getDefaultQuestionContent,
  getEmployerAdditionalQuestionContent,
  getOTCCopy,
  OTC_COPY_VERSION_MAP,
} from './constants';
import type { PropsFromRedux } from './container';
import { AsSeenSection } from './Sections/AsSeenSection';
import { CapsulePharmacySection } from './Sections/CapsulePharmacySection';
import { CarouselSection } from './Sections/CarouselSection';
import { FAQSection } from './Sections/FAQSection';
import { StepsSection } from './Sections/StepsSection';
import { FixedNav, MobileButton } from './styles';
import styles from './styles.module.scss';

const stickyAnchorElementId = 'stickyanchor';

// We have to use scss because CapsuleAccordion hasn't been rewritten to styled-components

const UnconnectedPromotionalLandingPage: FC<PropsFromRedux> = ({
  promoCode,
  segmentTrack,
  isCCA,
  isSinglePromoReferral,
  promoReferral,
  onRedirectToOnboarding,
}) => {
  const [mobileFooterOpacity, setMobileFooterOpacity] = useState(0);
  const [hasDispatchedViewEvent, setHasDispatchedViewEvent] = useState(false);

  const isSmallBreakpoint = useMediaQuery(theme.breakpoints.smallDown);

  const OTC_COPY = getOTCCopy();
  const copyKey = OTC_COPY_VERSION_MAP[promoCode];
  let otcCopy = OTC_COPY['default'];

  if (promoCode && OTC_COPY.hasOwnProperty(copyKey)) {
    otcCopy = OTC_COPY[copyKey];
  } else {
    console.error(`Unable to set OTC Copy. Promo Code: ${promoCode}. OTC_COPY: ${Object.keys(OTC_COPY)}`);
  }

  useEffect(() => {
    function handlePromoCodeLoaded() {
      const source = isSinglePromoReferral && promoReferral ? promoReferral : promoCode;
      segmentTrack(UNIFIED.viewPromoLandingPage, { promotion_value: promoCode, source });
      setHasDispatchedViewEvent(true);
    }
    if (!hasDispatchedViewEvent) {
      handlePromoCodeLoaded();
    }
  }, [hasDispatchedViewEvent, isSinglePromoReferral, promoCode, promoReferral, segmentTrack]);

  useEffect(() => {
    const getScrollDepth = throttle(() => {
      const scroll = window.scrollY || window.pageYOffset;

      const stickyAnchor = document.querySelector(`#${stickyAnchorElementId}`) as HTMLDivElement;
      const anchorOffSet = stickyAnchor?.offsetTop;
      if (!stickyAnchor) return;

      const shouldMakeMobileFooterTranslucent = anchorOffSet >= scroll;

      if (shouldMakeMobileFooterTranslucent) {
        setMobileFooterOpacity(0);
      } else {
        setMobileFooterOpacity(1);
      }
    }, 100);

    getScrollDepth();
    window.addEventListener('scroll', getScrollDepth);

    return () => {
      window.removeEventListener('scroll', getScrollDepth);
    };
  }, [promoCode, setMobileFooterOpacity, isSmallBreakpoint]);

  if (!otcCopy.formHeader) {
    return <Spinner centered />;
  }

  const {
    formHeader,
    formSubHeader,
    formCaption,
    offerSummary,
    buttonLabel,
    expandFirstFAQ,
    mobileCtaLabel,
    disclaimerLabel,
    otcItems,
    pharmacySectionHeader,
    faqSectionHeader,
    isEmployerOffering,
    additionalQuestionsSection = [],
    overrideQuestionsSection = [],
    explainerSectionType,
    additionalPharmacySection,
    hideFAQ,
    hideAsSeenInSection,
    hidePharmacySection,
    isGetStartedCTA,
  } = otcCopy;

  const handleMobileCTAClick = () => {
    segmentTrack(UNIFIED.selectOTCSignup, {});
    if (isGetStartedCTA) {
      onRedirectToOnboarding(promoCode);
    } else {
      smoothFocus(ELEMENT_IDS.firstField, 0, ELEMENT_IDS.OTC);
    }
  };

  const getQuestionContent = () => {
    if (overrideQuestionsSection.length > 0) {
      return overrideQuestionsSection;
    } else if (isEmployerOffering) {
      return [...additionalQuestionsSection, ...getEmployerAdditionalQuestionContent(), ...getDefaultQuestionContent()];
    } else {
      return getDefaultQuestionContent();
    }
  };

  return (
    <div>
      <Section color="capsuleBlue50" isHero={true}>
        <SignUpForm
          promoCode={promoCode}
          header={formHeader}
          subheader={formSubHeader}
          caption={formCaption}
          offerSummary={offerSummary}
          isEmployerOffering={isEmployerOffering}
          buttonLabel={buttonLabel}
          disclaimerLabel={disclaimerLabel}
          scrollId={ELEMENT_IDS.OTC}
          isGetStartedCTA={isGetStartedCTA}
        />
      </Section>

      <div id={stickyAnchorElementId} />

      <CarouselSection otcItems={otcItems} promoCode={promoCode} />

      {explainerSectionType === 'steps' && <StepsSection />}

      {additionalPharmacySection && (
        <CapsulePharmacySection
          color={'CapsuleWhite'}
          title={additionalPharmacySection.title}
          subtitle={additionalPharmacySection.subtitle}
          sections={additionalPharmacySection.sections}
        />
      )}

      {explainerSectionType !== 'steps' && !hidePharmacySection && !!pharmacySectionHeader && (
        <CapsulePharmacySection
          title={pharmacySectionHeader}
          sections={isEmployerOffering ? employerPharmacyContent : isCCA ? ccaPharmacyContent : defaultPharmacyContent}
        />
      )}

      {!hideAsSeenInSection && <AsSeenSection />}

      {!hideFAQ && (
        <FAQSection
          id={'faq'}
          header={faqSectionHeader}
          questionContent={getQuestionContent()}
          expandFirstQuestion={expandFirstFAQ}
          bodyClassName={styles.faqAccordionItemWrapperBody}
        />
      )}

      <FixedNav opacity={mobileFooterOpacity}>
        <MobileButton size="medium" block style="secondary" onClick={handleMobileCTAClick}>
          {mobileCtaLabel}
        </MobileButton>
      </FixedNav>
    </div>
  );
};

export const MemoizedUnconnectedPromotionalLandingPage = memo(UnconnectedPromotionalLandingPage);
